import * as React from "react";
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";

// Define input state variants
export type InputState = "default" | "error" | "warning" | "success" | "info";

// Extended input props with state support
export interface InputProps extends React.ComponentProps<"input"> {
  state?: InputState;
  helperText?: string;
  showIcon?: boolean;
}

// State-specific styling configuration
const stateStyles: Record<InputState, string> = {
  default: "border-input focus-visible:ring-ring",
  error:
    "border-error-border bg-error-light text-error-foreground placeholder:text-error-foreground/50 focus-visible:ring-error-border",
  warning:
    "border-warning-border bg-warning-light text-warning-foreground placeholder:text-warning-foreground/50 focus-visible:ring-warning-border",
  success:
    "border-success-border bg-success-light text-success-foreground placeholder:text-success-foreground/50 focus-visible:ring-success-border",
  info: "border-info-border bg-info-light text-info-foreground placeholder:text-info-foreground/50 focus-visible:ring-info-border",
};

// State icon mapping
const stateIcons: Record<Exclude<InputState, "default">, React.ElementType> = {
  error: AlertCircle,
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      state = "default",
      helperText,
      showIcon = true,
      ...props
    },
    ref,
  ) => {
    const StateIcon = state !== "default" ? stateIcons[state] : null;
    const hasIcon = showIcon && StateIcon;

    return (
      <div className="w-full">
        <div className="relative">
          <input
            type={type}
            className={cn(
              // Base styles
              "flex h-10 w-full rounded-md border bg-background px-3 py-2 text-base ring-offset-background",
              "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
              "placeholder:text-muted-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "transition-colors duration-200",
              "md:text-sm",
              // State-specific styles
              stateStyles[state],
              // Add padding for icon if present
              hasIcon && "pr-10",
              className,
            )}
            ref={ref}
            {...props}
          />
          {/* State icon positioned absolutely */}
          {hasIcon && StateIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <StateIcon
                className={cn(
                  "h-4 w-4",
                  state === "error" && "text-error",
                  state === "warning" && "text-warning",
                  state === "success" && "text-success",
                  state === "info" && "text-info",
                )}
              />
            </div>
          )}
        </div>
        {/* Helper text */}
        {helperText && (
          <p
            className={cn(
              "mt-1.5 text-xs",
              state === "default" && "text-muted-foreground",
              state === "error" && "text-error-foreground",
              state === "warning" && "text-warning-foreground",
              state === "success" && "text-success-foreground",
              state === "info" && "text-info-foreground",
            )}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input };
